import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { fetchQuery, requestSubscription, useFragment, useRelayEnvironment } from 'react-relay';

import Avatar from '@atlaskit/avatar/avatar';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import { ConfluencePageAri } from '@atlassian/ari';
import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

import { fg } from '@confluence/feature-gating';
import { useOpenCommentConversationIds } from '@confluence/comment-context/entry-points/openCommentConversations.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	PageAgentSessionNudgesQuery as PageAgentSessionNudgesQueryType,
	PageAgentSessionNudgesQuery$data,
} from './__generated__/PageAgentSessionNudgesQuery.graphql.ts';
import PageAgentSessionNudgesQuery from './__generated__/PageAgentSessionNudgesQuery.graphql.ts';
import type {
	PageAgentSessionNudgesSubscription as PageAgentSessionNudgesSubscriptionType,
	PageAgentSessionNudgesSubscription$data,
} from './__generated__/PageAgentSessionNudgesSubscription.graphql.ts';
import PageAgentSessionNudgesSubscription from './__generated__/PageAgentSessionNudgesSubscription.graphql.ts';
import type { RovoActivationFragment_query$key } from './__generated__/RovoActivationFragment_query.graphql.ts';
import type {
	RovoActivationQuery as RovoActivationQueryType,
	RovoActivationQuery$data,
} from './__generated__/RovoActivationQuery.graphql.ts';
import RovoActivationQuery from './__generated__/RovoActivationQuery.graphql.ts';
import { RovoActivationFragment } from './RovoActivationFragment.graphql.ts';

const PAGE_SIZE = 25;
const SESSION_REFRESH_DELAY_MS = 1000;

const getSessionUpdateKey = (sessionId: string, lastStateChangeAt?: number | null): string =>
	`${sessionId}:${lastStateChangeAt ?? 'unknown'}`;

const messages = defineMessages({
	fallbackTitle: {
		id: 'page-rovo-config-common.agent-session-nudge.fallback-title.ai-non-final',
		defaultMessage: 'Rovo conversation',
		description: 'Fallback title for an unread Rovo conversation shown beside the Rovo button.',
	},
});

const DEFAULT_AGENT_SESSION_NUDGE_INVOCATION_TYPES = [
	'CONFLUENCE_COMMENT_MENTION',
	'CONFLUENCE_CHAT_EDIT',
	'CONFLUENCE_INLINE_EDIT',
	'CONFLUENCE_CWR_EDIT',
	'CONFLUENCE_CWR_CREATE',
	'CONFLUENCE_CWR_EXISTING',
	'CONFLUENCE_CHAT',
] as const;

type AgentSessionNudgeState = 'INPUT_REQUIRED' | 'AUTH_REQUIRED' | 'ARTEFACT_READY';

const DEFAULT_AGENT_SESSION_NUDGE_STATES: readonly AgentSessionNudgeState[] = [
	'INPUT_REQUIRED',
	'AUTH_REQUIRED',
	'ARTEFACT_READY',
];

type AssociationQuery = NonNullable<
	PageAgentSessionNudgesQuery$data['agent_sessionAssociationQuery']
>;
export type AgentSessionAssociation = NonNullable<NonNullable<AssociationQuery['nodes']>[number]>;

type AgentSessionNudgeCandidate = Readonly<{
	agentPicture: string | null;
	agentName?: string | null;
	lastStateChangeAt?: number | null;
	channelId: string;
	channelName: string | null;
	sessionId: string;
}>;

type PageAgentSessionNudgePolicy = Readonly<{
	invocationTypes?: readonly string[];
	states?: readonly AgentSessionNudgeState[];
	unreadOnly?: boolean;
}>;

type PageAgentSessionNudgeOptions = Readonly<{
	rovoActivationFragmentRef?: RovoActivationFragment_query$key | null;
	contentId: string;
	enabled: boolean;
	policy?: PageAgentSessionNudgePolicy;
	onSessionClick?: (session: AgentSessionNudgeCandidate) => void;
}>;

type PageAgentSessionNudgeResult = Readonly<{
	nudges: readonly ConversationAction[];
	errors: readonly Error[];
}>;

// These values are interpolated inside quoted AQL literals. Escape the literal delimiters so an
// account ID can never produce malformed AQL or change the intended predicate structure.
const escapeAqlValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const buildAqlOrFilter = (field: string, values: readonly string[]): string =>
	values.map((value) => `${field} = '${escapeAqlValue(value)}'`).join('\n  OR ');

export const buildAgentSessionAql = (
	userId: string,
	pageAri: string,
	invocationTypes: readonly string[] = DEFAULT_AGENT_SESSION_NUDGE_INVOCATION_TYPES,
	states: readonly AgentSessionNudgeState[] = DEFAULT_AGENT_SESSION_NUDGE_STATES,
): string =>
	`userAccountId = '${escapeAqlValue(userId)}'\n` +
	`AND entityId = '${escapeAqlValue(pageAri)}'\n` +
	"AND deleted = 'false'\n" +
	`AND (\n  ${buildAqlOrFilter('state', states)}\n)\n` +
	`AND (\n  ${buildAqlOrFilter('invocationType', invocationTypes)}\n)`;

export const selectEligibleAgentSessions = (
	nodes: readonly (AgentSessionAssociation | null)[] | null | undefined,
	unreadOnly = true,
): AgentSessionNudgeCandidate[] => {
	// Multiple association records can resolve to the same conversation channel. Deduplicate them so
	// the same conversation is not registered as more than one nudge.
	const channelIds = new Set<string>();
	const eligible: AgentSessionNudgeCandidate[] = [];

	for (const association of nodes ?? []) {
		const session = association?.agentSession;
		const channel = session?.conversationChannel;
		// readStatus is resolved from the workspace-specific conversation channel and is not an
		// AgentSessionAssociation AQL field, so unread filtering must happen after hydration.
		if (
			!session?.id ||
			!channel?.id ||
			(unreadOnly && channel.readStatus !== false) ||
			channelIds.has(channel.id)
		) {
			continue;
		}

		channelIds.add(channel.id);
		eligible.push({
			agentPicture: session.agent?.picture ?? null,
			agentName: session.agent?.name,
			lastStateChangeAt: session.lastStateChangeAt,
			channelId: channel.id,
			channelName: channel.name ?? null,
			sessionId: session.id,
		});
	}

	// Preserve the server's lastStateChangeAt DESC order.
	return eligible;
};

export function usePageAgentSessionNudges({
	rovoActivationFragmentRef,
	contentId,
	enabled,
	policy,
	onSessionClick,
}: PageAgentSessionNudgeOptions): PageAgentSessionNudgeResult {
	// View Page supplies the activation data through ContentUnifiedQuery. Edit Page omits this
	// property and retains the standalone activation lookup until it has an equivalent parent query.
	const usesActivationFragment = rovoActivationFragmentRef != null;
	const { cloudId, isLoggedIn, userId } = useSessionData();
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const environment = useRelayEnvironment();
	const openCommentConversationIds = useOpenCommentConversationIds();
	const activationFragmentData = useFragment(
		RovoActivationFragment,
		rovoActivationFragmentRef ?? null,
	);
	const [activationQueryData, setActivationQueryData] = useState<RovoActivationQuery$data>();
	const [activationError, setActivationError] = useState<Error>();
	const [sessionError, setSessionError] = useState<Error>();
	const [subscriptionError, setSubscriptionError] = useState<Error>();

	const invocationTypes = policy?.invocationTypes ?? DEFAULT_AGENT_SESSION_NUDGE_INVOCATION_TYPES;
	const states = policy?.states ?? DEFAULT_AGENT_SESSION_NUDGE_STATES;
	const unreadOnly = policy?.unreadOnly ?? true;

	const pageAri = useMemo(
		() =>
			cloudId && contentId
				? ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString()
				: '',
		[cloudId, contentId],
	);

	const canLoadActivation = Boolean(
		!usesActivationFragment &&
		enabled &&
		isLoggedIn &&
		userId &&
		cloudId &&
		contentId &&
		invocationTypes.length &&
		states.length &&
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		fg('confluence_rovo_agent_session_nudges'),
	);

	useEffect(() => {
		if (!canLoadActivation) {
			setActivationQueryData(undefined);
			setActivationError(undefined);
			return;
		}

		const activationQuerySubscription = fetchQuery<RovoActivationQueryType>(
			environment,
			RovoActivationQuery,
			{ cloudId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				setActivationQueryData(data);
				setActivationError(undefined);
			},
			error: (error: Error) => setActivationError(error),
		});

		return () => activationQuerySubscription.unsubscribe();
	}, [canLoadActivation, cloudId, environment]);

	const activationData = usesActivationFragment ? activationFragmentData : activationQueryData;
	const rovoActivationId = activationData?.tenantContexts?.[0]?.activationIdByProduct?.active;
	const workspaceAri = rovoActivationId ? `ari:cloud:rovo::workspace/${rovoActivationId}` : '';
	const aqlQuery = useMemo(
		() =>
			userId && pageAri && invocationTypes.length && states.length
				? buildAgentSessionAql(userId, pageAri, invocationTypes, states)
				: '',
		[userId, pageAri, invocationTypes, states],
	);
	const hasAgentSessionNudgeContext = Boolean(
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		enabled && isLoggedIn && aqlQuery && workspaceAri && fg('confluence_rovo_agent_session_nudges'),
	);

	// A new context starts a new visit, including when route changes keep this hook mounted.
	const sessionContext = useMemo(
		() => ({ aqlQuery, environment, workspaceAri }),
		[aqlQuery, environment, workspaceAri],
	);
	const [sessionSnapshot, setSessionSnapshot] = useState<{
		context: typeof sessionContext;
		data: PageAgentSessionNudgesQuery$data;
		liveUpdates: ReadonlySet<string>;
	}>();

	useEffect(() => {
		setSessionSnapshot(undefined);
		setSessionError(undefined);
		setSubscriptionError(undefined);
		if (!hasAgentSessionNudgeContext || !userId || !cloudId) {
			return;
		}

		let disposed = false;
		let hasBaseline = false;
		let queryPending = false;
		let querySubscription: { unsubscribe: () => void } | undefined;
		let refreshTimer: ReturnType<typeof setTimeout> | undefined;
		const seenUpdates = new Set<string>();
		const liveUpdates = new Set<string>();
		// A background event remains ineligible even if a duplicate arrives after returning to the tab.
		let pendingSessions = new Map<string, boolean>();

		const scheduleRefresh = () => {
			clearTimeout(refreshTimer);
			refreshTimer = setTimeout(fetchSessions, SESSION_REFRESH_DELAY_MS);
		};
		const finishQuery = () => {
			queryPending = false;
			if (!disposed && pendingSessions.size) {
				scheduleRefresh();
			}
		};
		const fetchSessions = () => {
			// Never cancel the baseline request for a subscription event. Events arriving during a
			// request are kept for a subsequent refresh instead of losing their session IDs.
			if (disposed || queryPending) {
				return;
			}
			const receivedSessions = pendingSessions;
			pendingSessions = new Map();
			queryPending = true;
			querySubscription = fetchQuery<PageAgentSessionNudgesQueryType>(
				environment,
				PageAgentSessionNudgesQuery,
				{ aqlQuery, shardingContext: workspaceAri, first: PAGE_SIZE },
				// A cached baseline could make an older server update look new on navigation/reload.
				{ fetchPolicy: 'network-only' },
			).subscribe({
				next: (data) => {
					if (disposed) {
						return;
					}
					for (const node of data.agent_sessionAssociationQuery?.nodes ?? []) {
						const session = node?.agentSession;
						if (!session?.id) {
							continue;
						}
						const updateKey = getSessionUpdateKey(session.id, session.lastStateChangeAt);
						if (
							hasBaseline &&
							receivedSessions.get(session.id) &&
							pendingSessions.get(session.id) !== false &&
							!seenUpdates.has(updateKey)
						) {
							liveUpdates.add(updateKey);
						}
						seenUpdates.add(updateKey);
					}
					hasBaseline = true;
					setSessionSnapshot({ context: sessionContext, data, liveUpdates: new Set(liveUpdates) });
					setSessionError(undefined);
				},
				error: (error: Error) => {
					if (!disposed) {
						setSessionError(error);
					}
					finishQuery();
				},
				complete: finishQuery,
			});
		};

		const inboxSessionChangedSubscription =
			requestSubscription<PageAgentSessionNudgesSubscriptionType>(environment, {
				subscription: PageAgentSessionNudgesSubscription,
				variables: { accountId: userId, cloudId, workspaceAri },
				onNext: (data: PageAgentSessionNudgesSubscription$data | null | undefined) => {
					const sessionId = data?.convoai_onInboxSessionChanged?.sessionId;
					if (disposed || !sessionId) {
						return;
					}
					setSubscriptionError(undefined);
					pendingSessions.set(
						sessionId,
						pendingSessions.get(sessionId) !== false && document.visibilityState === 'visible',
					);
					scheduleRefresh();
				},
				onError: (error: Error) => {
					if (!disposed) {
						setSubscriptionError(error);
					}
				},
			});
		fetchSessions();

		return () => {
			disposed = true;
			clearTimeout(refreshTimer);
			querySubscription?.unsubscribe();
			inboxSessionChangedSubscription.dispose();
		};
	}, [
		cloudId,
		environment,
		aqlQuery,
		hasAgentSessionNudgeContext,
		sessionContext,
		userId,
		workspaceAri,
	]);

	const eligibleSessions = useMemo(() => {
		if (!hasAgentSessionNudgeContext || sessionSnapshot?.context !== sessionContext) {
			return [];
		}
		return selectEligibleAgentSessions(
			sessionSnapshot.data.agent_sessionAssociationQuery?.nodes,
			unreadOnly,
		).filter(
			(session) =>
				sessionSnapshot.liveUpdates.has(
					getSessionUpdateKey(session.sessionId, session.lastStateChangeAt),
				) && !openCommentConversationIds.has(session.channelId),
		);
	}, [
		hasAgentSessionNudgeContext,
		sessionContext,
		sessionSnapshot,
		unreadOnly,
		openCommentConversationIds,
	]);

	const nudges = useMemo<ConversationAction[]>(
		() =>
			eligibleSessions.map((session) => {
				const actionId = `agent-session-nudge-${session.sessionId}`;
				return {
					id: actionId,
					title: session.channelName || formatMessage(messages.fallbackTitle),
					proactiveNudgeStatus: 'unread',
					proactiveNudgeMetadata: {
						agentName: session.agentName ?? undefined,
						agentAvatarUrl: session.agentPicture ?? undefined,
						lastStateChangeAt: session.lastStateChangeAt ?? undefined,
					},
					icon: () =>
						session.agentPicture ? (
							<Avatar appearance="hexagon" name="" size="xxsmall" src={session.agentPicture} />
						) : (
							<></>
						),
					onClick: () => {
						if (onSessionClick) {
							onSessionClick(session);
							return;
						}

						publish({
							type: 'chat-open',
							data: { channelId: session.channelId },
							openChatMode: 'mini-modal',
							source: 'confluence-agent-session-nudge',
						});
					},
					type: 'ai-action',
					handleType: HandleActionType.Base,
					nudgeContext: {
						id: actionId,
						selfEligible: true,
						suppressionKey: session.channelId,
					},
				};
			}),
		[eligibleSessions, formatMessage, onSessionClick, publish],
	);
	const errors = useMemo(
		() =>
			[activationError, sessionError, subscriptionError].filter(
				(error): error is Error => error instanceof Error,
			),
		[activationError, sessionError, subscriptionError],
	);

	return {
		nudges,
		errors,
	};
}
